const POLL_INTERVAL_MS = 3000;
/**
 * Extraction renders every sheet and then makes one vision call per drawing
 * class, so a large upload legitimately runs for many minutes. At five minutes
 * this gave up on runs that were still working and later completed, which read
 * to the user as a failure and prompted a duplicate upload.
 */
const POLL_TIMEOUT_MS = 20 * 60 * 1000;

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const unwrapStatusPayload = (response) => response?.data?.data ?? response?.data ?? response ?? {};

/**
 * Polls GET /boq/extractData/status/:jobId until completed, failed, or timeout.
 * Returns result (same shape as legacy synchronous POST response.data).
 */
export async function pollBoqExtractJob(jobId, getStatus, { onStatus, signal } = {}) {
  if (!jobId) {
    throw new Error('Extraction job ID is missing');
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    if (signal?.aborted) {
      throw new Error('Drawing extraction cancelled');
    }

    const response = await getStatus(jobId);
    const statusPayload = unwrapStatusPayload(response);
    onStatus?.(statusPayload);

    if (statusPayload.status === 'completed') {
      if (!statusPayload.result) {
        throw new Error('Extraction completed but no result was returned');
      }
      return statusPayload.result;
    }

    // `retrying` is not terminal: the attempt failed but the queue will run it
    // again, so keep polling. Only `failed` means the job is finished and dead.
    if (statusPayload.status === 'failed') {
      throw new Error(statusPayload.error || 'Drawing extraction failed');
    }

    await sleep(POLL_INTERVAL_MS);
  }

  throw new Error(
    `Drawing extraction is taking longer than ${POLL_TIMEOUT_MS / 60000} minutes. It may still be running — reopen this accommodation shortly to check before uploading again.`
  );
}

export const BOQ_EXTRACT_POLL_INTERVAL_MS = POLL_INTERVAL_MS;
export const BOQ_EXTRACT_POLL_TIMEOUT_MS = POLL_TIMEOUT_MS;
